import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock3,
  FolderKanban,
  GraduationCap,
  Layers3,
  MessageSquareText,
  PlayCircle,
  ShieldCheck,
  ShoppingBag,
  Sparkles,
  UserRound,
} from "lucide-react";
import StarRating from "../components/ui/StarRating";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import logger from "../utils/logger.js";
import {
  courseFallbackImage,
  enrollInCourse,
  formatCurrency,
  getCourseDescription,
  getCourseImage,
  getCourseLearnList,
  getCoursePrice,
  getCourseRequirements,
  getCourseSummary,
  getCourseTitle,
  getMyCourses,
  getCourseDetails,
  listCourseReviews,
} from "../api/courses";
import "./DetailPages.css";

function splitLines(value) {
  return String(value || "")
    .split(/\r?\n|•/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export default function CourseDetails() {
  const { courseRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [enrollment, setEnrollment] = useState(null);
  const [openSectionId, setOpenSectionId] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [isImageViewerOpen, setIsImageViewerOpen] = useState(false);

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");

        const [details, courseReviews] = await Promise.all([
          getCourseDetails(courseRef),
          listCourseReviews(courseRef).catch(() => []),
        ]);
        if (!active) return;

        setCourse(details);
        setReviews(Array.isArray(courseReviews) ? courseReviews : []);
        setOpenSectionId(details?.sections?.[0]?.id || null);

        if (isLogged) {
          try {
            const myCourses = await getMyCourses();
            if (!active) return;
            const matched = myCourses.find(
              (item) => Number(item?.course?.id) === Number(details?.id)
            );
            setEnrollment(matched?.course ? matched : null);
          } catch (myCoursesError) {
            logger.warn("My courses fetch failed", myCoursesError);
          }
        } else {
          setEnrollment(null);
        }
      } catch (err) {
        logger.error("Course details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الكورس." : "Failed to load course details.");
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      active = false;
    };
  }, [courseRef, isAr, isLogged]);

  const title = useMemo(() => getCourseTitle(course, isAr), [course, isAr]);
  const summary = useMemo(() => getCourseSummary(course, isAr), [course, isAr]);
  const description = useMemo(() => getCourseDescription(course, isAr), [course, isAr]);
  const learnPoints = useMemo(() => splitLines(getCourseLearnList(course, isAr)), [course, isAr]);
  const requirements = useMemo(() => splitLines(getCourseRequirements(course, isAr)), [course, isAr]);
  const courseUrlRef = course?.slug || course?.id || courseRef;
  const courseImage = useMemo(() => getCourseImage(course), [course]);
  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") {
      return;
    }

    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = courseFallbackImage;
    event.currentTarget.classList.add("course-detail-image--contain");
    event.currentTarget.parentElement?.classList.add("course-detail-image-wrap--fallback");
  };

  const sections = Array.isArray(course?.sections) ? course.sections : [];
  const lessonsCount = Number(course?.lessons_count || 0);
  const reviewCount = Number(course?.reviews_count || reviews.length || 0);
  const courseFacts = [
    {
      key: "instructor",
      icon: UserRound,
      label: isAr ? "المدرب" : "Instructor",
      value: course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group"),
    },
    {
      key: "level",
      icon: GraduationCap,
      label: isAr ? "المستوى" : "Level",
      value: course?.level || "Beginner",
    },
    {
      key: "duration",
      icon: Clock3,
      label: isAr ? "المدة" : "Duration",
      value: course?.duration_text || (isAr ? "مرنة" : "Flexible"),
    },
    {
      key: "lessons",
      icon: BookOpen,
      label: isAr ? "الدروس" : "Lessons",
      value: String(lessonsCount),
    },
    {
      key: "sections",
      icon: Layers3,
      label: isAr ? "الأقسام" : "Sections",
      value: String(sections.length),
    },
    {
      key: "reviews",
      icon: MessageSquareText,
      label: isAr ? "التقييمات" : "Reviews",
      value: String(reviewCount),
    },
  ];

  const highlightStats = [
    {
      key: "duration",
      icon: Clock3,
      label: isAr ? "المدة" : "Duration",
      value: course?.duration_text || (isAr ? "مرنة" : "Flexible"),
    },
    {
      key: "lessons",
      icon: PlayCircle,
      label: isAr ? "عدد الدروس" : "Lesson Count",
      value: String(lessonsCount),
    },
    {
      key: "level",
      icon: GraduationCap,
      label: isAr ? "المستوى" : "Level",
      value: course?.level || "Beginner",
    },
    {
      key: "reviews",
      icon: MessageSquareText,
      label: isAr ? "المراجعات" : "Reviews",
      value: String(reviewCount),
    },
  ];

  const handleRequireAuth = () => {
    navigate("/login", { state: { from: location } });
  };

  const handleEnroll = async () => {
    if (!isLogged) {
      handleRequireAuth();
      return;
    }

    try {
      setActionLoading(true);
      setNotice("");
      setError("");
      await enrollInCourse(course.id);
      navigate(`/learn/${courseUrlRef}`);
    } catch (err) {
      logger.error("Course enroll error", err);
      setError(err?.message || (isAr ? "تعذر التسجيل في الكورس." : "Unable to enroll in course."));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckout = () => {
    if (!isLogged) {
      handleRequireAuth();
      return;
    }

    navigate(`/courses/${courseUrlRef}/checkout`);
  };

  useEffect(() => {
    if (!isImageViewerOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsImageViewerOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isImageViewerOpen]);

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جارِ تحميل الكورس..." : "Loading course..."}</p>
      </div>
    );
  }

  if (error && !course) {
    return <Navigate to="/courses" replace />;
  }

  const renderActionArea = () => {
    if (enrollment) {
      return (
        <>
          <Link to={`/learn/${courseUrlRef}`} className="btn btn-primary btn-lg course-detail-purchase-card__btn">
            {isAr ? "متابعة التعلم" : "Continue Learning"}
          </Link>
          <Link to="/my-courses" className="btn btn-outline-secondary btn-lg course-detail-purchase-card__btn">
            {isAr ? "كورساتي" : "My Courses"}
          </Link>
        </>
      );
    }

    if (course?.is_free) {
      return (
        <button
          type="button"
          className="btn btn-success btn-lg course-detail-purchase-card__btn"
          onClick={handleEnroll}
          disabled={actionLoading}
        >
          {actionLoading ? (isAr ? "جارِ التسجيل..." : "Enrolling...") : (isAr ? "ابدأ مجانًا" : "Start for Free")}
        </button>
      );
    }

    return (
      <button
        type="button"
        className="btn btn-primary btn-lg course-detail-purchase-card__btn"
        onClick={handleCheckout}
        disabled={actionLoading}
      >
        {actionLoading ? (isAr ? "جارِ التحويل..." : "Redirecting...") : (isAr ? "اشترِ الآن" : "Buy Now")}
      </button>
    );
  };

  return (
    <div className="detail-pages-container course-detail-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="course-detail-shell">
        <div className="course-detail-backbar">
          <Link to="/courses" className="course-detail-backlink">
            <ArrowLeft size={16} />
            {isAr ? "الرجوع إلى الكورسات" : "Back to Courses"}
          </Link>
        </div>

        <section className="course-detail-hero-v2">
          <div className="course-detail-hero-v2__main">
            <div
              className={`detail-image-wrapper course-detail-image-wrap course-detail-hero-v2__media ${
                courseImage.isFallback ? "course-detail-image-wrap--fallback" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={() => setIsImageViewerOpen(true)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  setIsImageViewerOpen(true);
                }
              }}
              aria-label={isAr ? "عرض الصورة بالحجم الكامل" : "Open full image"}
            >
              <img
                src={courseImage.src}
                alt={title}
                className={`detail-image course-detail-image ${courseImage.isFallback ? "course-detail-image--fallback-cover" : ""}`}
                onError={handleImageError}
              />
              <span className="course-detail-image-viewer-hint">
                {isAr ? "عرض كامل" : "Full view"}
              </span>
            </div>

            <div className="course-detail-hero-v2__copy">
              <div className="course-detail-topline">
                <span className="course-detail-pill">
                  <FolderKanban size={14} />
                  {course?.category || (isAr ? "كورس احترافي" : "Professional Course")}
                </span>
                <span className="course-detail-pill course-detail-pill--soft">
                  <GraduationCap size={14} />
                  {course?.level || "Beginner"}
                </span>
              </div>

              <h1 className="detail-info-title course-detail-hero-v2__title">{title}</h1>

              <div className="course-detail-hero-v2__meta">
                <div className="detail-rating">
                  <StarRating value={course?.average_rating || 0} size={18} showValue />
                  <span className="detail-reviews">({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                </div>
                <div className="course-detail-hero-v2__instructor">
                  <UserRound size={16} />
                  <span>{course?.instructor_name || (isAr ? "موسى جروب" : "Mousa Group")}</span>
                </div>
              </div>

              {summary ? <p className="detail-desc course-detail-hero-v2__summary">{summary}</p> : null}

              <div className="course-detail-quick-stats">
                {highlightStats.map((stat) => {
                  const Icon = stat.icon;
                  return (
                    <div key={stat.key} className="course-detail-quick-stat">
                      <span className="course-detail-quick-stat__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{stat.label}</small>
                        <strong>{stat.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="course-detail-purchase-card">
            <div className="course-detail-purchase-card__eyebrow">
              <Sparkles size={14} />
              {isAr ? "التحاق مرن" : "Flexible enrollment"}
            </div>

            <div className="detail-price course-detail-purchase-card__price">
              <span className="detail-current">
                {course?.is_free ? (isAr ? "مجاني" : "Free") : formatCurrency(getCoursePrice(course))}
              </span>
            </div>

            {notice ? <div className="alert alert-success rounded-3 mb-3">{notice}</div> : null}
            {error && course ? <div className="alert alert-danger rounded-3 mb-3">{error}</div> : null}

            <div className="course-detail-purchase-card__actions">
              {renderActionArea()}
            </div>

            <div className="course-detail-purchase-card__trust">
              <div>
                <ShieldCheck size={16} />
                <span>{isAr ? "وصول فوري بعد الشراء" : "Instant access after purchase"}</span>
              </div>
              <div>
                <CheckCircle2 size={16} />
                <span>{isAr ? "تنظيم واضح للدروس والتقدم" : "Clear lesson flow and progress"}</span>
              </div>
              <div>
                <ShoppingBag size={16} />
                <span>{isAr ? "مناسب للتعلم الذاتي العملي" : "Built for practical self-paced learning"}</span>
              </div>
            </div>
          </aside>
        </section>

        <section className="course-detail-layout-v2">
          <main className="course-detail-main-v2">
            {description && (
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "عن الكورس" : "About this course"}
                  </h3>
                </div>
                <div
                  className="detail-rich-text"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(description || (isAr ? "<p>لا يوجد وصف مفصل بعد.</p>" : "<p>No detailed description yet.</p>")),
                  }}
                />
              </article>
            )}

            <div className="course-detail-dual-grid">
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "ستتعلم" : "What you will learn"}
                  </h4>
                </div>
                {learnPoints.length ? (
                  <ul className="course-bullet-list course-bullet-list--modern">
                    {learnPoints.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">
                    {isAr ? "سيتم إضافة أهداف التعلم قريبًا." : "Learning outcomes will be added soon."}
                  </p>
                )}
              </article>

              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "المتطلبات" : "Requirements"}
                  </h4>
                </div>
                {requirements.length ? (
                  <ul className="course-bullet-list course-bullet-list--modern">
                    {requirements.map((item, index) => (
                      <li key={`${item}-${index}`}>{item}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-muted mb-0">
                    {isAr ? "لا توجد متطلبات خاصة لهذا الكورس." : "There are no special requirements for this course."}
                  </p>
                )}
              </article>
            </div>

            {sections.length > 0 && (
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "الأقسام والدروس" : "Sections & lessons"}
                  </h3>
                </div>
                <div className="accordion course-detail-accordion" id="course-sections">
                  {sections.map((section) => (
                    <div className="accordion-item border-0" key={section.id}>
                      <h5 className="accordion-header" id={`section-heading-${section.id}`}>
                        <button
                          className={`accordion-button ${openSectionId === section.id ? "" : "collapsed"}`}
                          type="button"
                          onClick={() =>
                            setOpenSectionId((current) => (current === section.id ? null : section.id))
                          }
                          aria-expanded={openSectionId === section.id ? "true" : "false"}
                          aria-controls={`section-collapse-${section.id}`}
                        >
                          {isAr ? section.title_ar || section.title_en : section.title_en || section.title_ar}
                        </button>
                      </h5>
                      <div
                        id={`section-collapse-${section.id}`}
                        className={`accordion-collapse collapse ${openSectionId === section.id ? "show" : ""}`}
                        aria-labelledby={`section-heading-${section.id}`}
                      >
                        <div className="accordion-body">
                          <ul className="list-group list-group-flush course-detail-lessons">
                            {(section.lessons || []).map((lesson) => (
                              <li
                                key={lesson.id}
                                className="list-group-item d-flex align-items-center justify-content-between gap-3"
                              >
                                <div>
                                  <div className="fw-semibold">
                                    {isAr ? lesson.title_ar || lesson.title_en : lesson.title_en || lesson.title_ar}
                                  </div>
                                  <div className="text-muted small">
                                    {lesson.is_preview ? (isAr ? "معاينة متاحة" : "Preview available") : (isAr ? "للمشتركين فقط" : "Enrolled students only")}
                                  </div>
                                </div>
                                {lesson.is_preview ? (
                                  <span className="badge text-bg-success">{isAr ? "معاينة" : "Preview"}</span>
                                ) : null}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </article>
            )}

            {reviews.length > 0 && (
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "التقييمات" : "Reviews"}
                  </h3>
                </div>
                <div className="detail-reviews-list course-detail-reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="detail-review-item course-detail-review-card">
                      <div className="d-flex justify-content-between mb-2 align-items-start gap-2">
                        <strong>{review.user?.name || (isAr ? "طالب" : "Student")}</strong>
                        <StarRating value={review.rating || 0} size={14} showValue />
                      </div>
                      <p className="text-muted small mb-0">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </article>
            )}
          </main>

          <aside className="course-detail-side-v2">
            <article className="course-detail-side-card">
              <h4>{isAr ? "معلومات الكورس" : "Course Facts"}</h4>
              <div className="course-detail-facts">
                {courseFacts.map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-fact">
                      <span className="course-detail-fact__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            <article className="course-detail-side-card course-detail-side-card--accent">
              <h4>{isAr ? "لمن هذا الكورس" : "Who this is for"}</h4>
              <div className="course-detail-side-notes">
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "المطور الذي يريد مساراً عملياً منظماً" : "Developers who want a practical, structured path"}</span>
                </div>
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "من يبحث عن تطبيقات واقعية وليس شرحاً سطحياً" : "Learners who prefer real-world implementation over surface-level theory"}</span>
                </div>
                <div>
                  <CheckCircle2 size={16} />
                  <span>{isAr ? "من يريد تجربة تعلم حديثة وواضحة" : "Anyone looking for a clean, modern learning experience"}</span>
                </div>
              </div>
            </article>
          </aside>
        </section>
      </div>

      {isImageViewerOpen ? (
        <div
          className="course-detail-lightbox"
          onClick={() => setIsImageViewerOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={isAr ? "عرض صورة الكورس" : "Course image viewer"}
        >
          <button
            type="button"
            className="course-detail-lightbox__close"
            onClick={() => setIsImageViewerOpen(false)}
            aria-label={isAr ? "إغلاق" : "Close"}
          >
            ×
          </button>
          <img
            src={courseImage.src}
            alt={title}
            className="course-detail-lightbox__image"
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : null}
    </div>
  );
}
